// Utilitários para manipulação de datas sem problemas de fuso horário

/**
 * Converte uma data ISO para formato brasileiro sem problemas de fuso horário
 * @param dateString - Data em formato ISO (YYYY-MM-DD ou YYYY-MM-DDTHH:mm:ss)
 * @returns Data formatada em pt-BR (DD/MM/YYYY)
 */
export const formatDateBR = (dateString: string): string => {
  if (!dateString) return '';

  // Se a data já está no formato YYYY-MM-DD, usar diretamente
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  }

  // Para datas com horário, extrair apenas a parte da data para evitar problemas de fuso horário
  if (dateString.includes('T')) {
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
  }

  // Para outros formatos, tentar extrair a data manualmente
  const dateMatch = dateString.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (dateMatch) {
    const [, year, month, day] = dateMatch;
    return `${day}/${month}/${year}`;
  }

  // Fallback: usar new Date() (pode ter problemas de fuso horário)
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${day}/${month}/${year}`;
};

/**
 * Converte uma data brasileira para formato ISO para envio ao backend
 * @param dateBR - Data em formato brasileiro (DD/MM/YYYY)
 * @returns Data em formato ISO (YYYY-MM-DD)
 */
export const formatDateISO = (dateBR: string): string => {
  if (!dateBR) return '';

  // Se já está no formato ISO, retornar como está
  if (dateBR.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateBR;
  }

  // Converter DD/MM/YYYY para YYYY-MM-DD
  const [day, month, year] = dateBR.split('/');
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

/**
 * Normaliza uma data para evitar problemas de fuso horário
 * @param dateString - Data em qualquer formato
 * @returns Data normalizada em formato ISO (YYYY-MM-DD)
 */
export const normalizeDate = (dateString: string): string => {
  if (!dateString) return '';

  // Se já está no formato correto, retornar
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateString;
  }

  // Se tem horário, extrair apenas a data
  if (dateString.includes('T')) {
    return dateString.split('T')[0];
  }

  // Se está no formato brasileiro, converter
  if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    return formatDateISO(dateString);
  }

  // Para outros formatos, tentar criar uma data e extrair apenas a parte da data
  try {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return dateString; // Retornar como está se não conseguir converter
  }
};

/**
 * Verifica se uma data é válida
 * @param dateString - Data em qualquer formato
 * @returns true se a data é válida
 */
export const isValidDate = (dateString: string): boolean => {
  if (!dateString) return false;

  try {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  } catch {
    return false;
  }
};
